// Thin JSON client for the Rails API. Same-origin cookie auth; CSRF token seeded
// from the <meta> tag and refreshed from every response header (login's
// reset_session rotates it, so a stale token would fail the next mutating request).
import { requestReauthentication } from "@/stores/reauthentication";

export class ApiError extends Error {
  constructor(status, body) {
    super(body?.error?.message || `Verzoek mislukt (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.code = body?.error?.code ?? null;
    this.errors = body?.errors ?? null;
    this.body = body;
  }
}

let csrfToken =
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? null;

function rememberCsrf(response) {
  const fresh = response.headers.get("X-CSRF-Token");
  if (fresh) csrfToken = fresh;
}

// A 403 carrying this code comes from RecentAuthentication#require_recent_authentication,
// which refuses the request in a before_action — so the action never ran, nothing
// changed, and re-issuing the very same call once the reader has confirmed who they are
// is safe. That is the whole reason the step-up lives here instead of in each caller:
// the reader stays on the page they were on, answers one question in a dialog, and the
// thing they pressed simply happens. Backing out throws the original error, so the form
// or row that fired it still shows its own message and its own retry.
const REAUTHENTICATION_REQUIRED = "reauthentication_required";

async function request(method, path, body, replayable = true) {
  const headers = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (!["GET", "HEAD"].includes(method) && csrfToken) headers["X-CSRF-Token"] = csrfToken;

  const response = await fetch(path, {
    method,
    headers,
    credentials: "same-origin",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  rememberCsrf(response);

  // Read the body as text first: `head :created`/`head :no_content` answer a JSON
  // request with a JSON content-type but an *empty* body, and response.json()
  // would throw on that. Only parse when there's actually something to parse.
  const contentType = response.headers.get("Content-Type") ?? "";
  const text = await response.text();
  const data = text && contentType.includes("application/json") ? JSON.parse(text) : null;

  if (!response.ok) {
    const error = new ApiError(response.status, data);
    // `replayable` guards the recursion: the replay itself is never replayed, so a
    // guard that keeps refusing surfaces as the error it is instead of a loop.
    if (replayable && error.code === REAUTHENTICATION_REQUIRED && (await requestReauthentication())) {
      return request(method, path, body, false);
    }
    throw error;
  }
  return data;
}

export const api = {
  get: (path) => request("GET", path),
  post: (path, body) => request("POST", path, body),
  patch: (path, body) => request("PATCH", path, body),
  delete: (path, body) => request("DELETE", path, body),
};
